import { createContext, FC, PropsWithChildren, useContext } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';

import { ProjectUnauthorized } from '@/components/ErrorScreens/ProjectErrors';
import environment from '@/environment';
import {
  DevStagingProdEnvsResponse,
  useAdminDevStagingProdEnvs,
  useAdminEnvironment,
} from '@/hooks/admin/environments';
import { useProject } from '@/hooks/admin/project';
import { ApiV3Response } from '@/hooks/admin/types';
import { useLinkToExplorer } from '@/hooks/links';
import { ProjectScope, useRawParams, useValidatedParams } from '@/hooks/params';
import { useResolveProjectAndEnvironmentId } from '@/hooks/resolveProjectAndEnvironmentId';
import { ParsedUniqueId } from '@/typings/parseUniqueId';
import { UrlEnvironmentType } from '@/typings/url';
import { DbtCloudAccountData, useDbtCloudAccount } from './DbtCloudProvider';
import { useDevelopmentContext } from './DevelopmentContextProvider';

export type DbtCloudProject = {
  name: string;
  id: number;
  defaultEnvironmentId?: number | undefined;
};

type DbtCloudProjectData = Omit<
  DbtCloudAccountData,
  'getProjectName' | 'hasProjectAccess' | 'dbtCloudProjects' | 'hasAccountFeature'
> & {
  projectName: string;
  projectEnvs: DevStagingProdEnvsResponse | undefined;
  defaultEnvironmentId: number | 'NO_DEFAULT_ENVIRONMENT' | undefined;
  defaultEnvironmentType: UrlEnvironmentType | undefined;
  accountId: number;
  projectId: number;
  environmentId: number;
  uniqueId: ParsedUniqueId | undefined;
  dbtUniqueId: string | undefined;
  enableModelQueryHistory: boolean;
  isInContext: boolean;
  isMounted: boolean;
  determiningProjectAccess: boolean;
};

const DbtCloudProjectContext = createContext<DbtCloudProjectData>({
  projectName: 'My Project',
  defaultEnvironmentId: undefined,
  defaultEnvironmentType: undefined,
  accountId: 0,
  account: undefined,
  projectId: 0,
  environmentId: 0,
  uniqueId: undefined,
  dbtUniqueId: undefined,
  projectEnvs: undefined,
  accountName: '',
  isInContext: false,
  hasDeveloperLicense: false,
  enableModelQueryHistory: false,
  appContext: 'dbt-cloud',
  isAdmin: false,
  isMounted: false,
  determiningProjectAccess: true,
  userHasPermissions: () => false,
  user: undefined,
  userId: 0, // What is a reasonable default?
});

/**
 * This hook should only be used inside of project/environment scoped
 * URLs. It guarantees that a projectId and environmentId are present.
 */
export const useDbtCloudProject = () => {
  const ctx = useContext(DbtCloudProjectContext);
  return ctx;
};

export type DbtCloudProjectParams = {
  projectId: number;
  environmentId: number;
  environmentType: UrlEnvironmentType;
  uniqueId: ParsedUniqueId | undefined;
};

export const DbtCloudProjectProviderImpl: FC<
  PropsWithChildren<{ params?: DbtCloudProjectParams }>
> = ({ children, params }) => {
  const navigate = useNavigate();
  const getEnvironmentUrl = useLinkToExplorer('project');
  const remainder = useParams()['*'] + window.location.search;
  const dbtCloud = useDbtCloudAccount();
  const isCatalogPreviewEnabled = dbtCloud.hasAccountFeature('explorerAccountUI');

  const rawParams = useRawParams();
  const validatedParams = useValidatedParams();

  const allParams = {
    accountId: dbtCloud.accountId,
    projectId: params?.projectId ?? rawParams.projectId,
    environmentId: params?.environmentId,
    environmentType: params?.environmentType ?? rawParams.environmentType,
    defaultEnvironmentId: dbtCloud.defaultEnvironmentId,
    uniqueId: params?.uniqueId ?? validatedParams.uniqueId,
    dbtUniqueId: params?.uniqueId?.raw ?? validatedParams.dbtUniqueId,
  };

  const defaultEnvironmentType = useAdminEnvironment(
    allParams.defaultEnvironmentId,
    allParams.accountId,
  ).data?.deployment_type as UrlEnvironmentType;
  const projectEnvs = useAdminDevStagingProdEnvs({
    accountId: allParams.accountId,
    projectId: allParams.projectId,
  });
  if (
    projectEnvs.data &&
    defaultEnvironmentType &&
    projectEnvs.data[allParams.environmentType] == null &&
    !isCatalogPreviewEnabled
  ) {
    const envScope: ProjectScope = {
      accountId: allParams.accountId || 0,
      projectId: allParams.projectId || 0,
      environmentType: defaultEnvironmentType,
    };
    const redirectUrl = getEnvironmentUrl(envScope) + remainder;
    navigate(redirectUrl);
  }
  const currentEnvironmentQuery = useResolveProjectAndEnvironmentId(allParams);
  const currentEnvironment = currentEnvironmentQuery?.data;
  const devData = useDevelopmentContext();

  const projectId = allParams.projectId;
  let accountId = allParams.accountId;
  let projectName = useProject({
    accountId,
    projectId,
  }).data?.name;

  let environmentId: any = allParams.environmentId ?? undefined;

  // If we're in dev mode, we will use dev data if we're in the relevant project
  if (devData.isDev && accountId === devData.accountId) {
    if (projectId) {
      // attempt to find the project in the mock data and get the environment id
      // TODO: should we only lookup mockProject if `devData.shouldMockProjects` is true?
      let environmentIdKey;
      switch (allParams.environmentType) {
        case 'production':
          environmentIdKey = 'productionEnvironmentId' as const;
          break;
        case 'staging':
          environmentIdKey = 'stagingEnvironmentId' as const;
          break;
        case 'development':
          environmentIdKey = 'devEnvironmentId' as const;
          break;
      }
      const mockProject = devData.mockProjects.find((p) => p.projectId === projectId);
      const mockProjectEnvironmentId = mockProject?.[environmentIdKey];
      environmentId =
        mockProjectEnvironmentId ||
        devData.environmentId || // typically productionEnvironmentId
        currentEnvironment?.environmentId;
    }

    projectName = devData.projectName || projectName;
  }

  if (!environmentId) {
    // this either means we're not in dev mode or dev mode couldn't find environmentId
    // if we can't make the currentEnvironmentQuery, throw the error
    if (
      currentEnvironmentQuery?.error &&
      currentEnvironmentQuery.error.response?.status
    ) {
      // if you reach here in dev mode, you either don't have a valid VITE_DBT_CLOUD_API_KEY
      // or you don't have an entry in projects.ts

      // eslint-disable-next-line no-console
      console.error(currentEnvironmentQuery.error);

      const projectUnauthorized = [403, 404].includes(
        currentEnvironmentQuery.error.response.status,
      );
      if (
        projectUnauthorized &&
        (environment.CYPRESS || environment.SHOULD_RENDER_PROJECT_UNAUTHORIZED)
      ) {
        return (
          <ProjectUnauthorized accountId={accountId || 0} projectId={projectId || 0} />
        );
      }

      // if we failed to determine project access and it's not an auth error,
      // throw the error to show a user message
      const userMessage = (
        currentEnvironmentQuery.error.response as unknown as ApiV3Response<any>
      )?.data?.status?.user_message;
      throw new Error(
        `Failed to determine project access due to query error: ${userMessage}`,
      );
    }

    accountId = currentEnvironment?.accountId ?? accountId;
    environmentId = currentEnvironment?.environmentId ?? environmentId;
  }

  return (
    <DbtCloudProjectContext.Provider
      key={`${dbtCloud.defaultEnvironmentId}|${allParams.projectId}`}
      value={{
        ...dbtCloud,
        defaultEnvironmentType: defaultEnvironmentType as UrlEnvironmentType, // TODO: determine if this is what we want to do
        projectName: projectName ?? '',
        accountId: accountId ?? 0,
        environmentId: environmentId || 0,
        uniqueId: allParams.uniqueId,
        dbtUniqueId: allParams.dbtUniqueId,
        /**
         * determiningProjectAccess is true when we are resolving environmentId to determine project access
         * determiningProjectAccess is false when we successfully resolved the environmentId (meaning we have project access, avoiding ProjectUnauthorized)
         *
         * Most of the times, children components should handle `determiningProjectAccess is true` by returning null.
         * Without doing this, children components could potentially show a loading state (skeleton components) before potentially
         * showing ProjectUnauthorized, causing a flicker in the UI.
         *
         * Therefore, show loading state only after project access is determined.
         */
        determiningProjectAccess: currentEnvironmentQuery?.isFetching ?? true,
        projectEnvs: projectEnvs.data,
        projectId: projectId || 0,
        enableModelQueryHistory:
          dbtCloud.hasAccountFeature('explorerQueryHistory') &&
          (currentEnvironment?.enableModelQueryHistory ?? false),
        isInContext: true,
        isMounted: !!environmentId, // For now, this is the only condition that blocks the provider being fully mounted
      }}
    >
      {children}
    </DbtCloudProjectContext.Provider>
  );
};

export const DbtCloudProjectProvider = () => {
  return <DbtCloudProjectProviderImpl children={<Outlet />} />;
};

export const DbtCloudProjectProviderWithParams: FC<
  PropsWithChildren<{ params: DbtCloudProjectParams }>
> = ({ children, ...props }) => {
  return (
    <DbtCloudProjectProviderImpl {...props}>{children}</DbtCloudProjectProviderImpl>
  );
};
